import React, { useCallback, useEffect } from 'react';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTutorial } from '../contexts/TutorialContext';

// Définition des étapes du tutoriel
const TUTORIAL_STEPS = [
  // Étape 1: Introduction - Tableau de bord
  {
    target: '[data-testid="dashboard-page"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Bienvenue dans GestMob !</h3>
        <p>Ce guide va vous présenter les fonctionnalités principales de l'application de gestion de mobilier.</p>
        <p className="mt-2 text-sm text-slate-500">Cliquez sur "Suivant" pour continuer.</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    route: '/dashboard'
  },
  // Étape 2: KPIs
  {
    target: '[data-testid="kpi-total"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Indicateurs clés (KPIs)</h3>
        <p>Le tableau de bord affiche les statistiques importantes de votre parc mobilier : total, affecté, en stock et en maintenance.</p>
      </div>
    ),
    placement: 'bottom',
    route: '/dashboard'
  },
  // Étape 3: Bouton ajouter mobilier
  {
    target: '[data-testid="add-furniture-btn"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Ajouter du mobilier</h3>
        <p>Utilisez ce bouton pour enregistrer un nouveau meuble dans votre base de données.</p>
      </div>
    ),
    placement: 'bottom',
    route: '/dashboard'
  },
  // Étape 4: Navigation vers liste mobilier
  {
    target: '[data-testid="furniture-list-page"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Liste du mobilier</h3>
        <p>Cette page affiche tous les meubles enregistrés avec leurs informations : code-barres, type, marque, localisation et état.</p>
        <p className="mt-2 text-sm text-slate-500">Vous pouvez filtrer et rechercher dans la liste.</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    route: '/furniture'
  },
  // Étape 5: Recherche
  {
    target: '[data-testid="search-input"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Recherche</h3>
        <p>Recherchez rapidement un mobilier par code-barres, marque ou modèle.</p>
      </div>
    ),
    placement: 'bottom',
    route: '/furniture'
  },
  // Étape 6: Filtres
  {
    target: '[data-testid="filter-type"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Filtres</h3>
        <p>Utilisez les filtres pour affiner votre recherche par type, état ou localisation.</p>
      </div>
    ),
    placement: 'bottom',
    route: '/furniture'
  },
  // Étape 7: Scanner
  {
    target: '[data-testid="scan-page"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Scanner de code-barres</h3>
        <p>Cette page vous permet de scanner les codes-barres pour identifier rapidement un mobilier.</p>
        <p className="mt-2 text-sm text-slate-500">Vous pouvez utiliser la caméra de votre appareil ou un lecteur physique.</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    route: '/scan'
  },
  // Étape 8: Bouton scanner caméra
  {
    target: '[data-testid="camera-scan-btn"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Scanner avec la caméra</h3>
        <p>Cliquez ici pour ouvrir le scanner et pointer vers un code-barres (QR Code, Code 128, EAN...).</p>
      </div>
    ),
    placement: 'bottom',
    route: '/scan'
  },
  // Étape 9: Saisie manuelle
  {
    target: '[data-testid="barcode-input"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Saisie manuelle</h3>
        <p>Vous pouvez aussi saisir le code-barres manuellement ou connecter un lecteur physique USB/Bluetooth.</p>
      </div>
    ),
    placement: 'bottom',
    route: '/scan'
  },
  // Étape 10: Inventaire
  {
    target: '[data-testid="inventory-page"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Gestion des inventaires</h3>
        <p>Créez des campagnes d'inventaire physique pour vérifier la présence et l'emplacement de votre mobilier.</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    route: '/inventory'
  },
  // Étape 11: Créer inventaire
  {
    target: '[data-testid="new-inventory-btn"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Nouvel inventaire</h3>
        <p>Cliquez ici pour créer une nouvelle campagne d'inventaire sur un site.</p>
      </div>
    ),
    placement: 'bottom',
    route: '/inventory'
  },
  // Étape 12: Rapports
  {
    target: '[data-testid="reports-page"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Rapports et exports</h3>
        <p>Générez des rapports détaillés et exportez vos données pour la comptabilité.</p>
      </div>
    ),
    placement: 'center',
    disableBeacon: true,
    route: '/reports'
  },
  // Étape 13: Export CSV
  {
    target: '[data-testid="export-csv-btn"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Export des données</h3>
        <p>Exportez vos données au format CSV ou JSON pour les utiliser dans d'autres logiciels.</p>
      </div>
    ),
    placement: 'bottom',
    route: '/reports'
  },
  // Étape 14: Exports comptables
  {
    target: '[data-testid="exports-comptables-tab"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Exports comptables</h3>
        <p>L'onglet "Exports Comptables" vous permet de télécharger les données d'immobilisations, mouvements et inventaires pour votre logiciel comptable.</p>
      </div>
    ),
    placement: 'bottom',
    route: '/reports'
  },
  // Étape finale
  {
    target: '[data-testid="tutorial-help-btn"]',
    content: (
      <div>
        <h3 className="font-bold text-lg mb-2">Tutoriel terminé !</h3>
        <p>Vous connaissez maintenant les fonctionnalités principales de GestMob.</p>
        <p className="mt-2 text-sm text-slate-500">Vous pouvez relancer ce guide à tout moment en cliquant sur le bouton "Guide".</p>
        <p className="mt-2">Bonne gestion de votre mobilier !</p>
      </div>
    ),
    placement: 'bottom',
    disableBeacon: true,
    route: '/dashboard'
  }
];

// Styles personnalisés pour Joyride
const joyrideStyles = {
  options: {
    arrowColor: '#ffffff',
    backgroundColor: '#ffffff',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    primaryColor: '#2563eb',
    spotlightShadow: '0 0 15px rgba(37, 99, 235, 0.5)',
    textColor: '#1e293b',
    width: 380,
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: 12,
    padding: 20,
  },
  tooltipContainer: {
    textAlign: 'left',
  },
  tooltipTitle: {
    marginBottom: 8,
  },
  tooltipContent: {
    padding: '8px 0',
  },
  buttonNext: {
    backgroundColor: '#2563eb',
    borderRadius: 8,
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 600,
  },
  buttonBack: {
    color: '#64748b',
    marginRight: 10,
    fontSize: '14px',
  },
  buttonSkip: {
    color: '#94a3b8',
    fontSize: '13px',
  },
  buttonClose: {
    color: '#64748b',
  },
  spotlight: {
    borderRadius: 8,
  },
  beacon: {
    display: 'none'
  }
};

// Texte français pour les boutons
const locale = {
  back: 'Précédent',
  close: 'Fermer',
  last: 'Terminer',
  next: 'Suivant',
  open: 'Ouvrir',
  skip: 'Passer le guide'
};

const Tutorial = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isRunning, stepIndex, setStepIndex, stopTutorial } = useTutorial();

  // Gestion des callbacks Joyride
  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, status, type } = data;
    
    console.log('Joyride callback:', { action, index, status, type });
    
    // Si le tutoriel est terminé ou fermé
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      stopTutorial();
      return;
    }
    
    // Si on ferme
    if (action === ACTIONS.CLOSE) {
      stopTutorial();
      return;
    }
    
    // Gestion de la navigation entre étapes
    if (type === EVENTS.STEP_AFTER) {
      let nextStepIndex;
      
      if (action === ACTIONS.PREV) {
        nextStepIndex = index - 1;
      } else if (action === ACTIONS.NEXT) {
        nextStepIndex = index + 1;
      } else {
        return;
      }
      
      if (nextStepIndex >= 0 && nextStepIndex < TUTORIAL_STEPS.length) {
        const nextStep = TUTORIAL_STEPS[nextStepIndex];
        
        // Si la route change, naviguer d'abord
        if (nextStep.route && nextStep.route !== location.pathname) {
          navigate(nextStep.route);
          // Attendre un peu que la page charge avant de changer l'étape
          setTimeout(() => {
            setStepIndex(nextStepIndex);
          }, 500);
        } else {
          setStepIndex(nextStepIndex);
        }
      }
    }
  }, [location.pathname, navigate, setStepIndex, stopTutorial]);

  // Synchroniser la route avec l'étape actuelle au démarrage
  useEffect(() => {
    if (isRunning && TUTORIAL_STEPS[stepIndex]) {
      const currentStep = TUTORIAL_STEPS[stepIndex];
      if (currentStep.route && currentStep.route !== location.pathname) {
        navigate(currentStep.route);
      }
    }
  }, [isRunning]);

  if (!isRunning) return null;

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      hideCloseButton={false}
      run={isRunning}
      scrollToFirstStep
      showProgress
      showSkipButton
      stepIndex={stepIndex}
      steps={TUTORIAL_STEPS}
      styles={joyrideStyles}
      locale={locale}
      disableOverlayClose={false}
      spotlightClicks={true}
      disableScrolling={false}
    />
  );
};

export default Tutorial;
